/* =============================================================================
   EXTORIL — HIYAZAKURA
   ambient.js — the wall behind the page
   -----------------------------------------------------------------------------
   One fixed, pointer-transparent layer BEHIND the content:

     · an ink-brush cherry bough, drawn procedurally from a seeded RNG (the same
       bough on every load), anchored off the top-right corner of the viewport;
     · a few sakura petals drifting down, which grow slightly in number and
       presence as the hero's cup chills.

   Usage
     <link rel="stylesheet" href="ambient.css">
     <script type="module" src="ambient.js"></script>
   That is enough: the module mounts itself once document.body exists.

     import mountAmbient from './ambient.js';
     const ambient = mountAmbient({ seed: 7, petals: [8, 14] });
     ambient.destroy();
     mountAmbient({ enabled: false });    // also removes an already mounted layer
     <html data-hz-ambient="off">         // suppresses the automatic mount

   Options (all optional)
     enabled  true      false renders nothing and tears down a mounted layer
     root     body      element the layer is appended to
     seed     number    RNG seed for the bough and the petal field
     petals   [10, 18]  [min, max] petals: min always, max at full bloom (cap 18)
     branch   true      draw the bough
     drift    true      draw the petals (never under prefers-reduced-motion)
     zIndex   -1        z-index of the layer

   Handle
     { el, destroy(), setBloom(0..1), pause(), play(), bloom }

   Reads (all optional, none required): --hz-bloom written inline on <html> by
   app.js, --hz-bloom on .hz-chill, or [data-hz-phase] on .hz-hero / .hz-chill.

   Cost
     · The petals move in a requestAnimationFrame loop paced to 24 ticks a
       second (the loop re-arms through a timeout, so the browser produces no
       main-thread frames in between). A tick is one transform string and one
       inline-style write per visible petal (10 at rest, 18 at full bloom) on a
       plain element that owns a compositor layer: no layout read, no paint. Measured against CSS animations: a composited
       CSS animation forces a 60 Hz main-thread lifecycle for as long as ANY
       IntersectionObserver exists on the page — including the browser's own
       one for <img loading="lazy"> — so this loop is the cheaper option here.
     · Bloom arrives through a MutationObserver on the inline style of <html>.
       The read is an inline-style lookup (no getComputedStyle on the hot path,
       no layout), the write is one custom property on the layer; CSS derives
       the petal count, the petal opacity and the blossom opacity from it.
     · The loop stops when the tab is hidden (visibilitychange). The layer is
       fixed and viewport-sized, so it is off-screen exactly when the document
       is; an IntersectionObserver would only add the 60 Hz cost above.
     · prefers-reduced-motion never builds the drift: the bough stays, static.
     · No imports, no requests, no images, no fonts, no absolute paths.
   ========================================================================== */

const CLASS = 'hz-ambient';
const VIEW_W = 1000;
const VIEW_H = 720;
const PETAL_CAP = 18;

/* ---- Small helpers ------------------------------------------------------- */

const clamp01 = v => (v < 0 ? 0 : v > 1 ? 1 : v);
const lerp = (a, b, t) => a + (b - a) * t;
const f1 = n => String(Math.round(n * 10) / 10);
const f2 = n => (Math.round(n * 100) / 100).toFixed(2);

/* One sakura petal: base at the origin, tip pointing up (-y), notched.
   M p0  C p1 p2 p3  C p4 p5 p6  C p7 p8 p9  C p10 p11 p12  Z                */
const PETAL_PTS = [
  [0, 0], [-3.4, -1.4], [-5.6, -5.8], [-4.6, -9.2], [-3.9, -11.4], [-1.7, -11.6], [0, -9.6],
  [1.7, -11.6], [3.9, -11.4], [4.6, -9.2], [5.6, -5.8], [3.4, -1.4], [0, 0]
];

function petalPath(transform) {
  const q = PETAL_PTS.map(transform).map(p => f2(p[0]) + ' ' + f2(p[1]));
  return 'M' + q[0] + 'C' + q[1] + ' ' + q[2] + ' ' + q[3] + 'C' + q[4] + ' ' + q[5] + ' ' + q[6] +
         'C' + q[7] + ' ' + q[8] + ' ' + q[9] + 'C' + q[10] + ' ' + q[11] + ' ' + q[12] + 'Z';
}

const PETAL_D = petalPath(p => p);

/* Five petals, each pushed 1.4 out from the centre and turned 72 degrees,
   then scaled, rotated and moved into place: one subpath per petal. */
function flowerAt(x, y, sc, deg) {
  let d = '';
  for (let i = 0; i < 5; i++) {
    const a = (deg + i * 72) * Math.PI / 180;
    const ca = Math.cos(a);
    const sa = Math.sin(a);
    d += petalPath(([px, py]) => {
      py -= 1.4;
      return [x + (px * ca - py * sa) * sc, y + (px * sa + py * ca) * sc];
    });
  }
  return d;
}

const PHASE_BLOOM = { plain: 0, first: 0.15, opening: 0.5, full: 0.9 };

const DEFAULTS = {
  enabled: true,
  root: null,
  seed: 3,
  petals: [10, PETAL_CAP],
  branch: true,
  drift: true,
  zIndex: -1
};

let current = null;   /* the handle this module instance mounted */
let manual = false;   /* mountAmbient() was called by someone other than the auto-mount */

/* mulberry32 — tiny, seedable, good enough for a branch. */
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* A cubic from (x0,y0) leaving at angle a0, arriving at angle a1, of length len. */
function cubic(x0, y0, a0, a1, len) {
  const am = (a0 + a1) / 2;
  const k = len * 0.38;
  const x3 = x0 + Math.cos(am) * len;
  const y3 = y0 + Math.sin(am) * len;
  return {
    x0, y0,
    x1: x0 + Math.cos(a0) * k, y1: y0 + Math.sin(a0) * k,
    x2: x3 - Math.cos(a1) * k, y2: y3 - Math.sin(a1) * k,
    x3, y3
  };
}

const pathOf = c =>
  'M' + f1(c.x0) + ' ' + f1(c.y0) +
  'C' + f1(c.x1) + ' ' + f1(c.y1) + ' ' + f1(c.x2) + ' ' + f1(c.y2) + ' ' + f1(c.x3) + ' ' + f1(c.y3);

/* de Casteljau: the [t, 1] portion of a cubic. */
function tail(c, t) {
  const l = (a, b) => a + (b - a) * t;
  const x01 = l(c.x0, c.x1), y01 = l(c.y0, c.y1);
  const x12 = l(c.x1, c.x2), y12 = l(c.y1, c.y2);
  const x23 = l(c.x2, c.x3), y23 = l(c.y2, c.y3);
  const x012 = l(x01, x12), y012 = l(y01, y12);
  const x123 = l(x12, x23), y123 = l(y12, y23);
  return { x0: l(x012, x123), y0: l(y012, y123), x1: x123, y1: y123, x2: x23, y2: y23, x3: c.x3, y3: c.y3 };
}

/* ---- Symbols ------------------------------------------------------------- */

function defsSvg() {
  return (
    '<svg class="hz-ambient__defs" width="0" height="0" aria-hidden="true" focusable="false"><defs>' +
      '<path id="hz-ambient-petal" d="' + PETAL_D + '"/>' +
    '</defs></svg>'
  );
}

/* ---- The bough ----------------------------------------------------------- *
   A turtle grows one main stem from off the top-right corner, down and to the
   left, in a few cubic segments joined by sharp elbows (sumi-e boughs are
   knotted, not smooth). Side limbs fork at the elbows at wide angles, short
   and curling upward; twigs fork off the limbs, shorter still. The stem and
   the limbs are tapered ink ribbons with a hint of brush pressure and a pool
   of ink at every elbow; the twigs are hairlines. Blossom clusters sit on the
   twigs; up to five terminal twigs are tipped in gold.                       */

function bezAt(c, t) {
  const u = 1 - t;
  return {
    x: u * u * u * c.x0 + 3 * u * u * t * c.x1 + 3 * u * t * t * c.x2 + t * t * t * c.x3,
    y: u * u * u * c.y0 + 3 * u * u * t * c.y1 + 3 * u * t * t * c.y2 + t * t * t * c.y3
  };
}

function bezTan(c, t) {
  const u = 1 - t;
  return {
    x: 3 * u * u * (c.x1 - c.x0) + 6 * u * t * (c.x2 - c.x1) + 3 * t * t * (c.x3 - c.x2),
    y: 3 * u * u * (c.y1 - c.y0) + 6 * u * t * (c.y2 - c.y1) + 3 * t * t * (c.y3 - c.y2)
  };
}

/* A closed, tapered shape around a cubic: width w0 at the start, w1 at the end. */
function ribbon(c, w0, w1, pressure) {
  const N = 10;
  const left = [];
  const right = [];
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    const p = bezAt(c, t);
    const d = bezTan(c, t);
    const m = Math.hypot(d.x, d.y) || 1;
    const nx = -d.y / m;
    const ny = d.x / m;
    const h = lerp(w0, w1, t) * (pressure ? pressure(t) : 1) / 2;
    left.push(f1(p.x + nx * h) + ' ' + f1(p.y + ny * h));
    right.push(f1(p.x - nx * h) + ' ' + f1(p.y - ny * h));
  }
  return 'M' + left.join('L') + 'L' + right.reverse().join('L') + 'Z';
}

function drawBough(rand) {
  const ribbons = [];
  const lines = [];
  const knots = [];
  const flowers = [];
  const tips = [];
  const inside = (x, y, m) => x > m && x < VIEW_W - m && y > m && y < VIEW_H - m;
  const phase = rand() * 6.28;
  const pressure = t => 1 + 0.16 * Math.sin(t * 5.7 + phase);

  /* per generation: segments, length share of the parent, elbow angle,
     curl per segment, fork chance, blossom chance (at a knot / at the tip),
     blossom scale, blossom alpha */
  const GEN = [
    { segs: 6, share: [1, 1],       kink: 0.34, curl: -0.03, fork: 0,    bloom: [0,    0   ], fs: 0,    fa: 0    },
    { segs: 3, share: [0.22, 0.32], kink: 0.22, curl: 0.09,  fork: 0.75, bloom: [0.35, 0.6 ], fs: 0.26, fa: 0.12 },
    { segs: 2, share: [0.36, 0.5],  kink: 0.28, curl: 0.10,  fork: 0.6,  bloom: [0.6,  0.85], fs: 0.32, fa: 0.15 },
    { segs: 2, share: [0.4, 0.55],  kink: 0.3,  curl: 0.12,  fork: 0,    bloom: [0.8,  0.95], fs: 0.36, fa: 0.17 }
  ];
  const MAIN_LEN = 800;

  function bloomAt(x, y, depth, atTip) {
    const g = GEN[depth];
    const roll = rand();
    if (roll >= g.bloom[atTip ? 1 : 0] || !inside(x, y, 4)) return;
    const n = 1 + (rand() < 0.55 ? 1 : 0) + (rand() < 0.35 ? 1 : 0);
    for (let j = 0; j < n && flowers.length < 48; j++) {
      flowers.push({
        x: x + (rand() - 0.5) * 14,
        y: y + (rand() - 0.5) * 14,
        s: g.fs * (0.8 + rand() * 0.5),
        r: rand() * 360,
        a: g.fa + rand() * 0.06
      });
    }
  }

  function grow(x, y, a, len, w0, w1, depth) {
    const g = GEN[depth];
    const segs = g.segs;
    let kinkSign = rand() < 0.5 ? 1 : -1;
    for (let i = 0; i < segs; i++) {
      const L = (len / segs) * (0.8 + rand() * 0.4);
      const a1 = a + g.curl + (rand() - 0.5) * 0.24;
      const c = cubic(x, y, a, a1, L);
      const wa = lerp(w0, w1, i / segs);
      const wb = lerp(w0, w1, (i + 1) / segs);
      if (depth <= 1) ribbons.push(ribbon(c, wa, wb, depth === 0 ? pressure : null));
      else lines.push({ d: pathOf(c), w: (wa + wb) / 2 });
      x = c.x3;
      y = c.y3;

      const last = i === segs - 1;
      if (depth === 0 && !last) knots.push({ x, y, r: wb * 0.62 });
      if (depth >= 1) bloomAt(x, y, depth, last);
      if (last) { tips.push({ c, w: wb, depth }); break; }
      if (!inside(x, y, -60)) break;            /* wandered off the sheet */

      /* fork at the elbow: the stem alternates sides, finer wood is random */
      if (depth < GEN.length - 1) {
        let fork = false;
        let side = 1;
        if (depth === 0) {
          fork = i >= 1 && i <= segs - 2 && (i % 2 === 1 || rand() < 0.5);
          side = i % 2 === 1 ? 1 : -1;
        } else {
          fork = rand() < g.fork;
          side = rand() < 0.5 ? 1 : -1;
        }
        if (fork) {
          const ng = GEN[depth + 1];
          const ca = a1 + side * (0.95 + rand() * 0.45);          /* 55..80 degrees off */
          const cl = (depth === 0 ? MAIN_LEN : len) * lerp(ng.share[0], ng.share[1], rand());
          if (depth === 0) grow(x, y, ca, cl, wb * 0.5, 0.8, 1);
          else if (depth === 1) grow(x, y, ca, cl, 0.75, 0.62, 2);
          else grow(x, y, ca, cl, 0.6, 0.5, 3);
        }
      }

      /* the elbow: alternating along the stem, random on the finer wood */
      const ks = depth === 0 ? kinkSign : (rand() < 0.5 ? 1 : -1);
      kinkSign = -kinkSign;
      a = a1 + ks * g.kink * (0.7 + rand() * 0.6);
    }
  }

  /* The stem: off the top-right corner, heading down-left. Widths are viewBox
     units (the ribbons scale with the bough); the hairline twigs do not. */
  grow(1060, -40, 2.7, MAIN_LEN, 5.6, 1.7, 0);

  /* Gold: the outer half of up to five terminal twigs that end on the sheet. */
  const candidates = tips.filter(t => t.depth >= 2 && inside(t.c.x3, t.c.y3, 30));
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const s = candidates[i]; candidates[i] = candidates[j]; candidates[j] = s;
  }
  const gold = candidates.slice(0, 5).map(t => ({ c: tail(t.c, 0.45), w: Math.max(0.6, t.w) }));

  const open = cls =>
    '<svg class="' + cls + '" viewBox="0 0 ' + VIEW_W + ' ' + VIEW_H + '" ' +
    'preserveAspectRatio="xMaxYMin meet" aria-hidden="true" focusable="false">';

  /* Every element costs a style recalc whenever app.js writes to <html>, so
     the whole bough is a handful of paths: ribbons and knots share one, twigs
     are bucketed by width, gold is one, blossoms are bucketed by alpha. */
  const knotPath = k =>
    'M' + f1(k.x - k.r) + ' ' + f1(k.y) +
    'a' + f2(k.r) + ' ' + f2(k.r) + ' 0 1 0 ' + f2(k.r * 2) + ' 0' +
    'a' + f2(k.r) + ' ' + f2(k.r) + ' 0 1 0 ' + f2(-k.r * 2) + ' 0Z';
  const wood = ribbons.join('') + knots.map(knotPath).join('');
  const fine = lines.filter(p => p.w >= 0.65).map(p => p.d).join('');
  const finer = lines.filter(p => p.w < 0.65).map(p => p.d).join('');
  const goldD = gold.map(p => pathOf(p.c)).join('');

  const branch =
    open('hz-ambient__branch') +
      '<path d="' + wood + '" fill="var(--hz-ink-3,#1E1E23)" stroke="var(--hz-ink-3,#1E1E23)" stroke-width="0.5" stroke-linejoin="round"/>' +
      '<g fill="none" stroke="var(--hz-ink-3,#1E1E23)" stroke-linecap="round" stroke-linejoin="round">' +
        (fine ? '<path d="' + fine + '" stroke-width="0.7"/>' : '') +
        (finer ? '<path d="' + finer + '" stroke-width="0.55"/>' : '') +
      '</g>' +
      (goldD ? '<path d="' + goldD + '" fill="none" stroke="var(--hz-gold,#C9A227)" stroke-width="0.7" stroke-opacity="0.32" stroke-linecap="round"/>' : '') +
    '</svg>';

  const buckets = [[0.12, ''], [0.16, ''], [0.21, '']];
  for (let i = 0; i < flowers.length; i++) {
    const o = flowers[i];
    const b = o.a < 0.145 ? 0 : o.a < 0.19 ? 1 : 2;
    buckets[b][1] += flowerAt(o.x, o.y, o.s, o.r);
  }
  const blossom =
    open('hz-ambient__blossom') +
      '<g fill="var(--hz-sakura,#F4A7C3)">' +
        buckets.map(b => (b[1] ? '<path d="' + b[1] + '" fill-opacity="' + f2(b[0]) + '"/>' : '')).join('') +
      '</g>' +
    '</svg>';

  return branch + blossom;
}

/* ---- The drift ----------------------------------------------------------- *
   `max` petals are created once. The first `min` are always present; the rest
   carry a threshold t in (0.05..0.85) and appear, one by one, as the bloom
   passes it (CSS does the gating and the fade). Petals are 8..14px, alpha
   0.14..0.32, weighted toward the right half of the sheet, under the bough.
   Each petal falls at its own speed, glides on a sine, rocks into the glide,
   and turns very slowly; below the sheet it re-enters above it.             */

const pickX = rand => (rand() < 0.6 ? 0.44 + rand() * 0.54 : 0.02 + rand() * 0.42);

function buildDrift(rand, min, max) {
  const petals = [];
  let html = '';
  for (let i = 0; i < max; i++) {
    const size = 8 + Math.floor(rand() * 7);
    const alpha = 0.14 + rand() * 0.18;
    const t = i < min ? -1 : 0.05 + (max - min > 1 ? (i - min) / (max - min - 1) : 0) * 0.8;
    petals.push({
      el: null,
      size,
      t,
      seen: 0,
      x: pickX(rand),                                   /* fraction of the width */
      y: rand() * 1.1 - 0.05,                           /* fraction of the height, spread at load */
      vy: 22 + rand() * 16 - (size - 8),                /* px per second */
      amp: 8 + rand() * 20,                             /* glide, px */
      w: (Math.PI * 2) / (3.5 + rand() * 3.5),          /* glide angular speed */
      phase: rand() * Math.PI * 2,
      rock: 18 + rand() * 32,                           /* degrees, into the glide */
      base: rand() * 360,
      spin: (rand() < 0.5 ? -1 : 1) * (2 + rand() * 6)  /* degrees per second */
    });
    html +=
      '<div class="hz-ambient__petal" style="--hz-ambient-t:' + f2(t) + '">' +
        '<svg width="' + size + '" height="' + size + '" viewBox="-7 -12.5 14 14" aria-hidden="true" focusable="false">' +
          '<use href="#hz-ambient-petal" fill="var(--hz-sakura,#F4A7C3)" fill-opacity="' + f2(alpha) + '"/>' +
        '</svg>' +
      '</div>';
  }
  return { html: '<div class="hz-ambient__drift">' + html + '</div>', petals };
}

/* ---- Mount --------------------------------------------------------------- */

function inertHandle() {
  return { el: null, bloom: 0, destroy() {}, setBloom() {}, pause() {}, play() {} };
}

function teardownAll() {
  if (current) current.destroy();
  if (typeof document === 'undefined') return;
  const stray = document.querySelectorAll('.' + CLASS);
  for (let i = 0; i < stray.length; i++) {
    const h = stray[i].hzAmbient;          /* a twin module instance (e.g. two fingerprints) */
    if (h && typeof h.destroy === 'function') h.destroy();
    else stray[i].remove();
  }
}

function mount(options) {
  const opts = Object.assign({}, DEFAULTS, options || {});
  teardownAll();
  if (!opts.enabled || typeof document === 'undefined' || !document.body) return inertHandle();

  const seed = (Number(opts.seed) >>> 0) || DEFAULTS.seed;
  let pMin, pMax;
  if (Array.isArray(opts.petals)) { pMin = opts.petals[0]; pMax = opts.petals[1]; }
  else { pMax = opts.petals; pMin = Math.round(opts.petals * 0.6); }
  pMax = Math.max(0, Math.min(PETAL_CAP, Math.round(Number(pMax) || 0)));
  pMin = Math.max(0, Math.min(pMax, Math.round(Number(pMin) || 0)));

  const layer = document.createElement('div');
  layer.className = CLASS;
  layer.setAttribute('aria-hidden', 'true');
  layer.setAttribute('data-hz-ambient', 'mounted');
  if (opts.zIndex !== DEFAULTS.zIndex) layer.style.setProperty('--hz-ambient-z', String(opts.zIndex));
  layer.innerHTML = defsSvg() + (opts.branch ? drawBough(mulberry32(seed)) : '');
  (opts.root || document.body).appendChild(layer);

  /* -- drift, gated by prefers-reduced-motion ----------------------------- */
  const mq = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
  const petalRand = mulberry32(seed ^ 0x9e3779b9);
  let driftEl = null;
  let petals = [];
  let W = 0;
  let H = 0;

  function measure() {
    W = layer.clientWidth;
    H = layer.clientHeight;
  }

  function syncDrift() {
    const want = opts.drift && pMax > 0 && !(mq && mq.matches);
    if (want && !driftEl) {
      const built = buildDrift(petalRand, pMin, pMax);
      layer.insertAdjacentHTML('beforeend', built.html);
      driftEl = layer.lastElementChild;
      petals = built.petals;
      const els = driftEl.children;
      measure();
      for (let i = 0; i < petals.length; i++) {
        petals[i].el = els[i];
        petals[i].y *= H;                                  /* fraction -> px, spread over the sheet */
      }
      step(0);
    } else if (!want && driftEl) {
      driftEl.remove();
      driftEl = null;
      petals = [];
    }
    syncLoop();
  }

  /* -- the loop: 24 ticks a second, nothing between ticks ---------------- */
  const TICK = 1000 / 24;
  let raf = 0;
  let timer = 0;
  let last = 0;
  let clock = 0;
  let held = false;
  let bloom = -1;                               /* last applied bloom, 0..1 */

  function step(dt) {
    clock += dt;
    const b = bloom < 0 ? 0 : bloom;
    for (let i = 0; i < petals.length; i++) {
      const p = petals[i];
      if (b >= p.t) p.seen = clock;
      else if (clock - p.seen > 2) continue;          /* gated off and faded: leave it be */
      p.y += p.vy * dt;
      if (p.y > H + p.size) {
        p.y = -p.size - petalRand() * 80;
        p.x = pickX(petalRand);
      }
      const ph = clock * p.w + p.phase;
      const x = p.x * W + p.amp * Math.sin(ph) - p.size / 2;
      const rot = p.base + p.rock * Math.cos(ph) + p.spin * clock;
      p.el.style.transform =
        'translate3d(' + x.toFixed(1) + 'px,' + (p.y - p.size / 2).toFixed(1) + 'px,0) rotate(' + rot.toFixed(1) + 'deg)';
    }
  }

  function frame(now) {
    raf = 0;
    const elapsed = now - last;
    if (elapsed < TICK - 1) {                     /* too early: come back at the next tick, not the next frame */
      timer = setTimeout(arm, TICK - elapsed);
      return;
    }
    last = now;
    step(Math.min(elapsed, 100) / 1000);
    arm();
  }

  function arm() {
    timer = 0;
    if (!raf) raf = requestAnimationFrame(frame);
  }

  function running() {
    return !!driftEl && !held && !document.hidden;
  }

  function syncLoop() {
    if (running()) {
      if (!raf && !timer) { last = performance.now(); arm(); }
    } else {
      if (raf) cancelAnimationFrame(raf);
      if (timer) clearTimeout(timer);
      raf = 0;
      timer = 0;
    }
  }

  const onVisibility = () => syncLoop();
  document.addEventListener('visibilitychange', onVisibility);

  let resizeTimer = 0;
  const onResize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(measure, 200);
  };
  addEventListener('resize', onResize, { passive: true });

  const onMotion = () => syncDrift();
  if (mq) {
    if (mq.addEventListener) mq.addEventListener('change', onMotion);
    else if (mq.addListener) mq.addListener(onMotion);
  }
  syncDrift();

  /* -- bloom: read what the hero publishes, write one property ------------ */
  const html = document.documentElement;
  const hero = document.querySelector('.hz-hero');
  const chill = document.querySelector('.hz-chill');

  /* stepped = the value came from a phase word, so petals cross their
     thresholds in jumps and CSS gets to ease them; a continuous --hz-bloom
     needs no transition (and retargeting 18 of them per drag frame is the
     single most expensive thing this layer could do). */
  let stepped = false;

  function readBloom() {
    let v = parseFloat(html.style.getPropertyValue('--hz-bloom'));
    if (!isNaN(v)) { stepped = false; return clamp01(v); }
    if (chill) {
      v = parseFloat(chill.style.getPropertyValue('--hz-bloom'));
      if (!isNaN(v)) { stepped = false; return clamp01(v); }
    }
    const phased = (hero && hero.hasAttribute('data-hz-phase')) ? hero
      : (chill && chill.hasAttribute('data-hz-phase')) ? chill
      : document.querySelector('[data-hz-phase]');
    if (phased) {
      const p = PHASE_BLOOM[phased.getAttribute('data-hz-phase')];
      if (p !== undefined) { stepped = true; return p; }
    }
    v = parseFloat(getComputedStyle(html).getPropertyValue('--hz-bloom'));
    stepped = false;
    return isNaN(v) ? 0 : clamp01(v);
  }

  function applyBloom(v) {
    v = Math.round(clamp01(Number(v) || 0) * 100) / 100;
    layer.classList.toggle('hz-ambient--stepped', stepped);
    if (v === bloom) return;
    bloom = v;
    layer.style.setProperty('--hz-ambient-bloom', String(v));
    if (petals.length && running()) step(0);      /* place any petal that just switched on */
  }

  const onMutate = () => applyBloom(readBloom());
  const mo = typeof MutationObserver === 'function' ? new MutationObserver(onMutate) : null;
  if (mo) {
    mo.observe(html, { attributes: true, attributeFilter: ['style', 'data-hz-phase'] });
    if (hero) mo.observe(hero, { attributes: true, attributeFilter: ['data-hz-phase'] });
    if (chill && chill !== hero) mo.observe(chill, { attributes: true, attributeFilter: ['data-hz-phase'] });
  }
  applyBloom(readBloom());

  /* -- handle -------------------------------------------------------------- */
  let destroyed = false;
  const handle = {
    el: layer,
    get bloom() { return bloom < 0 ? 0 : bloom; },
    setBloom(v) { if (!destroyed) { stepped = true; applyBloom(v); } },
    pause() { held = true; syncLoop(); },
    play() { held = false; syncLoop(); },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      held = true;
      syncLoop();
      clearTimeout(resizeTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      removeEventListener('resize', onResize);
      if (mo) mo.disconnect();
      if (mq) {
        if (mq.removeEventListener) mq.removeEventListener('change', onMotion);
        else if (mq.removeListener) mq.removeListener(onMotion);
      }
      layer.hzAmbient = null;
      layer.remove();
      if (current === handle) current = null;
    }
  };
  layer.hzAmbient = handle;
  current = handle;
  return handle;
}

export default function mountAmbient(options) {
  manual = true;
  return mount(options);
}

/* ---- Auto-mount on import ------------------------------------------------ */

if (typeof document !== 'undefined' && document.documentElement &&
    document.documentElement.getAttribute('data-hz-ambient') !== 'off') {
  if (document.body) {
    mount();
  } else {
    document.addEventListener('DOMContentLoaded', () => { if (!manual && !current) mount(); }, { once: true });
  }
}
